import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, RefreshCw, XCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { adminApi } from "@/lib/admin-api";

function StatusRow({ label, ok, hint }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border/40 last:border-0">
      <div>
        <div className="text-sm font-medium">{label}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </div>
      {ok ? (
        <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-xs">
          <CheckCircle2 className="h-3.5 w-3.5" /> OK
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 text-xs">
          <XCircle className="h-3.5 w-3.5" /> Missing
        </span>
      )}
    </div>
  );
}

export default function AdminHealth() {
  const [health, setHealth] = useState(null);
  const [scraperProbe, setScraperProbe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const [h, p] = await Promise.all([
        adminApi.health(),
        adminApi.ops.scraperProbe().catch((err) => ({
          ok: false,
          error: err?.response?.data?.error || err?.message,
        })),
      ]);
      setHealth(h);
      setScraperProbe(p);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to load");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Probing services…
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-sm text-destructive">{error}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold">Health</h1>
          <p className="text-sm text-muted-foreground">
            Live probes for the database, snapshot scraper, and required env vars.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => load(true)} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
          Re-probe
        </Button>
      </div>

      <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Database</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Status</span>
              {health?.database?.ok ? (
                <Badge variant="default">healthy</Badge>
              ) : (
                <Badge variant="destructive">down</Badge>
              )}
            </div>
            {health?.database?.latencyMs != null && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Latency</span>
                <span className="tabular-nums">{health.database.latencyMs} ms</span>
              </div>
            )}
            {health?.database?.error && (
              <div className="text-xs text-destructive">{health.database.error}</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Snapshot scraper</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Status</span>
              {scraperProbe?.ok ? (
                <Badge variant="default">healthy</Badge>
              ) : (
                <Badge variant="destructive">unreachable</Badge>
              )}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">URL</span>
              <code className="text-xs">{scraperProbe?.url || health?.scraper?.url || "—"}</code>
            </div>
            {scraperProbe?.latencyMs != null && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Latency</span>
                <span className="tabular-nums">{scraperProbe.latencyMs} ms</span>
              </div>
            )}
            {scraperProbe?.error && (
              <div className="text-xs text-destructive">{scraperProbe.error}</div>
            )}
            {scraperProbe?.body && (
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer">Response body</summary>
                <pre className="mt-1 p-2 rounded bg-muted/40 overflow-auto text-[11px]">
                  {typeof scraperProbe.body === "string"
                    ? scraperProbe.body
                    : JSON.stringify(scraperProbe.body, null, 2)}
                </pre>
              </details>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            Environment variables{" "}
            <Badge variant="secondary" className="ml-1 text-[10px]">
              {health?.nodeEnv || "—"}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            {Object.entries(health?.env || {}).map(([key, present]) => (
              <StatusRow
                key={key}
                label={key}
                ok={Boolean(present)}
                hint={
                  key === "OPENAI_API_KEY"
                    ? "Required for Layer B classification"
                    : key === "META_ADS_ACCESS_TOKEN"
                      ? "Required for Meta ad sync"
                      : key === "SERPAPI_KEY"
                        ? "Required for Google ad sync"
                        : key === "SCRAPER_URL"
                          ? "Snapshot scraper sidecar"
                          : key === "RESEND_API_KEY"
                            ? "Required for first-winner emails"
                            : undefined
                }
              />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
